import clsx from 'clsx';
import { type ElementType, forwardRef, type ReactElement } from 'react';

import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { type DataTestId } from '../../core/types/data-props.js';
import { type MaskingProps } from '../../core/types/masking-props.js';
import { type PolymorphicComponentProps } from '../../core/types/polymorph.js';
import { type StylingProps } from '../../core/types/styling-props.js';
import { type WithChildren } from '../../core/types/with-children.js';
import {
  type FlexStyleProps,
  getFlexStyles,
} from '../../styles/getFlexStyles.js';

const COMPONENT_NAME = 'Flex';

/**
 * Flex own props.
 * @public
 */
export type FlexOwnProps = WithChildren &
  StylingProps &
  FlexStyleProps &
  DataTestId &
  MaskingProps &
  BehaviorTrackingProps;

/**
 * Merge own props with others inherited from the underlying element type.
 * @public
 */
export type FlexProps<E extends ElementType> = PolymorphicComponentProps<
  E,
  FlexOwnProps
>;

/**
 * The `Flex` component can be used to layout its children with Flexbox.
 * The component can be customized with flexbox props.
 * @public
 */
export const Flex: <E extends ElementType = 'div'>(
  props: FlexProps<E>,
) => ReactElement | null = /* @__PURE__ */ forwardRef(
  <E extends ElementType>(
    props: FlexProps<E>,
    forwardedRef: typeof props.ref,
  ) => {
    const {
      children,
      id,
      as,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      'data-dt-component': dataDtComponent = COMPONENT_NAME,
      ...remainingProps
    } = props;

    const [flexClassNames, flexStyles, nonFlexStyleProps] = getFlexStyles(
      remainingProps,
      { gap: 8 },
    );

    const [behavioralTrackingProps, restNonFlexProps] =
      useBehavioralTrackingProps(nonFlexStyleProps);

    const Component = as ?? 'div';

    return (
      <Component
        ref={forwardedRef}
        id={id}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        className={clsx(flexClassNames, consumerClassName)}
        style={{
          ...flexStyles,
          ...consumerStyle,
        }}
        {...restNonFlexProps}
        {...behavioralTrackingProps}
        data-dt-component={dataDtComponent}
      >
        {children}
      </Component>
    );
  },
);
